import { hiDPI, size } from "polished";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import ClientLogo from "../img/client-logo.svg?react";
import { default as GBFlag } from "../img/flags/gb.svg?react";
import { default as PTFlag } from "../img/flags/pt.svg?react";
import { getPlayerComp, IPlayerCompProps } from "../utils/player-comps";
import { getSite } from "../utils/sites";
import { getIsWebView } from "../utils/webView";
import { Wrapper } from "./Layout";
import Navigation from "./nav/Navigation";

const GameHeaderOuter = styled.header`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 152px;
  background-color: ${({ theme }) => theme.colors.primaryTwo};

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    height: 210px;
    background: linear-gradient(
      45deg,
      ${({ theme }) => theme.colors.primaryThree} 0 70%,
      ${({ theme }) => theme.colors.secondaryThree} 30% 100%
    );
  }
`;

const GameHeaderInner = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 90%;
  max-width: 1220px;
  margin-top: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    margin-top: ${({ theme }) => theme.space[3]};
  }
`;

const PlayerImage = styled.div<IPlayerCompProps>`
  position: absolute;
  bottom: 0;
  inset-inline-end: 0;
  width: 100%;
  height: 120px;
  background-image: url(${(props) => props.images.x1});
  background-position: 100% 0;
  background-repeat: no-repeat;
  background-size: contain;
  transform: scaleX(1);

  ${hiDPI(2)} {
    background-image: url(${(props) => props.images.x2});
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    inset-inline-end: 15px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    inset-inline-end: 50px;
    height: 135px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    inset-inline-end: 100px;
    height: 210px;

    ${hiDPI(2)} {
      background-image: url(${(props) => props.images.x2});
    }
  }
`;

const FlagList = styled.ul`
  position: absolute;
  top: 15px;
  inset-inline-end: 0;
  z-index: 2;
  display: flex;
  gap: ${({ theme }) => theme.space[2]};
  margin: 0;
  padding: 0;
  list-style-type: none;
`;

const FlagItem = styled.li`
  display: inherit;
  padding-inline-end: 1rem;
  line-height: 1;
  vertical-align: middle;

  &:not(:last-child) {
    border-inline-end: 1px solid white;
  }
`;

const FlagLink = styled.a`
  display: inherit;
`;

const FlagStyles = css`
  ${size(13, 20)}

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    ${size(17, 28)}
  }
`;

const StyledPTFlag = styled(PTFlag)`
  ${FlagStyles}
`;

const StyledGBFlag = styled(GBFlag)`
  ${FlagStyles}
`;

const HeaderLogo = styled.div`
  z-index: 1;
`;

const ClientLogoStyles = css`
  ${size(100, 96)}

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    ${size(159, 153)}
  }
`;

const StyledClientLogo = styled(ClientLogo)`
  ${ClientLogoStyles}
`;

const GameHeader = () => {
  const { t } = useTranslation();

  const site =
    getSite() === "main_pt"
      ? "https://fantasy.ligaportugal.pt"
      : "https://en.fantasy.ligaportugal.pt";

  if (getIsWebView()) {
    return (
      <>
        <Navigation />
        <Wrapper>
          <span id="webview"></span>
        </Wrapper>
      </>
    );
  }

  return (
    <>
      <GameHeaderOuter>
        <GameHeaderInner>
          <FlagList>
            <FlagItem>
              <FlagLink href="https://fantasy.ligaportugal.pt/">
                <StyledPTFlag />
              </FlagLink>
            </FlagItem>
            <FlagItem>
              <FlagLink href="https://en.fantasy.ligaportugal.pt">
                <StyledGBFlag />
              </FlagLink>
            </FlagItem>
          </FlagList>
          <PlayerImage images={getPlayerComp()} />
          <HeaderLogo>
            <a href={site} target="_blank" rel="noreferrer">
              <StyledClientLogo
                title={t("gameHeader.homeLink", "Go to home page")}
              />
            </a>
          </HeaderLogo>
        </GameHeaderInner>
      </GameHeaderOuter>
      <Navigation />
    </>
  );
};

export default GameHeader;
